import { useStore } from "@xyflow/react";
import { useEffect, useRef } from "react";
import { HelperLine } from "./types";

export type HelperLinesProps = {
  horizontal?: HelperLine;
  vertical?: HelperLine;
};

const FALLBACK_COLOR = "#3b82f6";

/**
 * Canvas overlay that draws the active alignment guides. Edge guides are
 * solid, center guides dashed. Ink follows `--canvas-selection` so guides
 * read as part of the selection language.
 */
export function HelperLinesRenderer({
  horizontal,
  vertical,
}: HelperLinesProps) {
  // Primitive selectors — an object selector without an equality function
  // re-renders on EVERY store update. This is a leaf component, so the
  // per-frame re-render while panning is cheap; the expensive part
  // (reallocating the canvas backing store) is guarded below.
  const width = useStore((state) => state.width);
  const height = useStore((state) => state.height);
  const tx = useStore((state) => state.transform[0]);
  const ty = useStore((state) => state.transform[1]);
  const tz = useStore((state) => state.transform[2]);
  const transform: [number, number, number] = [tx, ty, tz];

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sizeRef = useRef<{ w: number; h: number } | null>(null);
  const drewRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!ctx || !canvas) {
      return;
    }

    // Assigning canvas.width/height REALLOCATES the full backing store
    // (viewport × dpr² × 4 bytes — ~100MB+ per assignment on a large
    // window). Only do it when the size truly changed; otherwise clear.
    // An idle overlay (no guides, nothing drawn) skips the work entirely.
    const dpi = window.devicePixelRatio;
    const w = Math.floor(width * dpi);
    const h = Math.floor(height * dpi);
    const sized = sizeRef.current;
    const resized = !sized || sized.w !== w || sized.h !== h;

    if (!horizontal && !vertical && !drewRef.current && !resized) {
      return;
    }

    if (resized) {
      canvas.width = w;
      canvas.height = h;
      sizeRef.current = { w, h };
    } else {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, w, h);
    }
    ctx.setTransform(dpi, 0, 0, dpi, 0, 0);
    drewRef.current = false;

    if (!horizontal && !vertical) {
      return;
    }

    const guideColor =
      getComputedStyle(canvas).getPropertyValue("--canvas-selection").trim() ||
      FALLBACK_COLOR;
    ctx.strokeStyle = guideColor;

    if (vertical) {
      const x = vertical.position * transform[2] + transform[0];
      const [y1, y2] = vertical.span
        ? [
            vertical.span[0] * transform[2] + transform[1],
            vertical.span[1] * transform[2] + transform[1],
          ]
        : [0, height];
      ctx.beginPath();
      ctx.setLineDash(vertical.anchorName === "centerX" ? [5, 5] : []);
      ctx.moveTo(x, y1);
      ctx.lineTo(x, y2);
      ctx.stroke();
      drewRef.current = true;
    }

    if (horizontal) {
      const y = horizontal.position * transform[2] + transform[1];
      const [x1, x2] = horizontal.span
        ? [
            horizontal.span[0] * transform[2] + transform[0],
            horizontal.span[1] * transform[2] + transform[0],
          ]
        : [0, width];
      ctx.beginPath();
      ctx.setLineDash(horizontal.anchorName === "centerY" ? [5, 5] : []);
      ctx.moveTo(x1, y);
      ctx.lineTo(x2, y);
      ctx.stroke();
      drewRef.current = true;
    }
  }, [width, height, tx, ty, tz, horizontal, vertical]);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 z-10 h-full w-full"
    />
  );
}
